import React, { useEffect, useState, useRef } from "react";
import { api, API } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import {
    Database, Plus, Trash2, RefreshCcw, Upload, RotateCcw,
    Bot, Save, X, Search, Download, Sparkles, Send, Wand2, Check, BookOpen
} from "lucide-react";

// Which entity → which drill-down "kind" (see /accounts/:kind/:id route)
const LEDGER_KIND = {
    vendors: "vendor",
    clients: "client",
    bank_accounts: "bank",
    employees: "employee",
};

// ============ helpers ============
const isNumericLike = (v) => typeof v === "number" || (typeof v === "string" && v !== "" && !isNaN(v));

// Columns whose values are numeric-shaped but are NEVER money (phones, PAN, GSTIN, IFSC,
// account numbers, ID codes, PIN codes, dates as YYYYMMDD, etc.). We render these as
// plain digits with no ₹ prefix, no L/Cr suffix, no locale grouping.
const NON_MONETARY_COLUMN_REGEX = /(^|_)(phone|mobile|contact_no|whatsapp|alt_contact|pan|tan|cin|gstin|ifsc|micr|swift|udyam|account_no|acc_no|ac_no|a_c_no|bank_ac|account_number|aadhaar|aadhar|pin|pincode|zip|code|udin|epf|esi|pf_no|uan|hsn|sac|serial|sr_no|imei|barcode|gtin|nsdl_reg|sebi_reg|voter_id|passport|dl_no|dob|doj|dor|date|time|year|month|est_year|founded_year|reg_year|policy_no|invoice_no|challan_no|voucher_no|po_no|reference|ref_no|utr|cheque_no|chq_no|drivers_license|itc|sez|otp|token|id$|^id$)/i;

const formatCell = (v, columnName = "") => {
    if (v === null || v === undefined) return "—";
    const col = String(columnName || "").toLowerCase();
    // Any column matching the non-monetary regex → render as string, no currency formatting
    if (col && NON_MONETARY_COLUMN_REGEX.test(col)) {
        return String(v);
    }
    if (typeof v === "number") {
        if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
        if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
        if (Number.isInteger(v)) return v.toLocaleString("en-IN");
        return v.toFixed(2);
    }
    return String(v);
};

const parseInput = (raw, oldValue, columnName = "") => {
    if (raw === "") return "";
    const col = String(columnName || "").toLowerCase();
    // Never coerce identifier-like columns (phone, PAN, IFSC, etc.) into numbers,
    // otherwise they get displayed as ₹ later and lose leading zeros.
    if (col && NON_MONETARY_COLUMN_REGEX.test(col)) return raw;
    if (typeof oldValue === "number") {
        const n = Number(raw);
        return isNaN(n) ? raw : n;
    }
    // Auto-detect numeric strings
    if (isNumericLike(raw) && raw.match(/^-?\d+(\.\d+)?$/)) return Number(raw);
    return raw;
};

// ============ Inline editable cell ============
function EditableCell({ value, onSave, testId, columnName }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const inputRef = useRef(null);

    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

    const commit = () => {
        if (draft !== String(value ?? "")) {
            onSave(parseInput(draft, value, columnName));
        }
        setEditing(false);
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                data-testid={testId}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") commit();
                    else if (e.key === "Escape") setEditing(false);
                }}
                className="w-full bg-[var(--bg-base)] border border-[var(--brand-amber)] px-1.5 py-0.5 text-xs font-mono-fin text-[var(--text-primary)] focus:outline-none"
            />
        );
    }

    return (
        <span
            data-testid={testId}
            onDoubleClick={() => { setDraft(String(value ?? "")); setEditing(true); }}
            className="cursor-pointer hover:bg-panel px-1 py-0.5 rounded-sm inline-block min-w-[40px]"
            title="Double-click to edit"
        >
            {formatCell(value, columnName)}
        </span>
    );
}

// ============ Ask Agent Modal ============
function AskAgentModal({ open, onClose, row, entity }) {
    const [agentKey, setAgentKey] = useState("cfo_briefing");
    const [question, setQuestion] = useState("");
    const [response, setResponse] = useState("");
    const [busy, setBusy] = useState(false);
    const [agents, setAgents] = useState([]);

    useEffect(() => {
        if (open) {
            api.get("/agents").then((r) => setAgents(r.data.agents || []));
            setResponse("");
            setQuestion("");
        }
    }, [open]);

    const defaultQ = `Give me your opinion on this ${entity} record. What action should I take?`;

    const ask = async () => {
        const q = question.trim() || defaultQ;
        const fullMsg = `Regarding this ${entity} record:\n${JSON.stringify(row, null, 2)}\n\n${q}`;
        setBusy(true);
        setResponse("");
        try {
            const res = await fetch(`${API}/agents/${agentKey}/ask/stream`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: fullMsg }),
            });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith("data:")) continue;
                    try {
                        const evt = JSON.parse(line.slice(5).trim());
                        if (evt.type === "delta") setResponse((r) => r + evt.content);
                    } catch { /* ignore */ }
                }
            }
        } catch (e) {
            setResponse(`⚠️ ${e.message}`);
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-panel border border-panel max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="text-[var(--brand-amber)]" size={16} />
                        <div className="font-display font-bold text-[var(--text-primary)]">Ask an AI Agent</div>
                    </div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-3">
                    <div className="border border-panel bg-[var(--bg-base)] p-2 text-[10px] font-mono-fin text-[var(--text-primary)]/85 max-h-32 overflow-y-auto whitespace-pre-wrap">{JSON.stringify(row, null, 2)}</div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Agent</div>
                        <select
                            data-testid="ask-agent-picker"
                            value={agentKey}
                            onChange={(e) => setAgentKey(e.target.value)}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin"
                        >
                            {agents.map((a) => <option key={a.key} value={a.key}>{a.title}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Your question (optional)</div>
                        <textarea
                            data-testid="ask-agent-question"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder={defaultQ}
                            rows={2}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-body focus:border-[var(--brand-amber)] focus:outline-none"
                        />
                    </div>
                    <button
                        data-testid="ask-agent-send"
                        onClick={ask}
                        disabled={busy}
                        className="bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                        <Send size={12} /> {busy ? "Thinking…" : "Ask"}
                    </button>
                    {response && (
                        <div className="border border-panel bg-[var(--bg-base)] p-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] mb-2">// {agents.find(a => a.key === agentKey)?.title || agentKey}</div>
                            <div className="text-xs font-body whitespace-pre-wrap text-[var(--text-primary)]/90">{response || "▊"}</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============ Add Row Modal ============
function AddRowModal({ open, onClose, columns, primaryKey, onSave, entity, taxonomy }) {
    const [row, setRow] = useState({});

    useEffect(() => { if (open) setRow({}); }, [open]);

    if (!open) return null;

    const save = async () => {
        if (!row[primaryKey]) {
            toast.error(`Primary key '${primaryKey}' required`);
            return;
        }
        // Auto-coerce numerics — BUT keep phone/PAN/GSTIN/IFSC/account_no etc as strings
        // so they don't get displayed as ₹ later and don't lose leading zeros.
        const clean = {};
        for (const k of Object.keys(row)) {
            const v = row[k];
            if (v === "" || v === undefined) continue;
            const isNonMonetary = NON_MONETARY_COLUMN_REGEX.test(String(k).toLowerCase());
            if (!isNonMonetary && typeof v === "string" && v.match(/^-?\d+(\.\d+)?$/)) {
                clean[k] = Number(v);
            } else {
                clean[k] = v;
            }
        }
        await onSave(clean);
        onClose();
    };

    const isProject = entity === "projects";
    const isVendor = entity === "vendors";
    const isClient = entity === "clients";
    const isBank = entity === "bank_accounts";
    const isCash = entity === "cash_accounts";
    const isInv = entity === "inventory";
    const isMI = entity === "material_issues";

    // Dropdown maps — column name → taxonomy list key
    const DROPDOWNS = {
        projects: {
            category: taxonomy?.project_categories,
            sub_category: taxonomy?.project_sub_categories,
        },
        vendors: {
            category: taxonomy?.vendor_categories,
            dependency: taxonomy?.vendor_dependency,
            risk_grade: taxonomy?.vendor_risk_grade,
            msme_status: taxonomy?.msme_status,
            gst_treatment: taxonomy?.gst_treatment,
            tds_section: taxonomy?.tds_sections,
        },
        clients: {
            type: taxonomy?.client_types,
            sector: taxonomy?.client_sectors,
            msme_status: taxonomy?.msme_status,
            gst_treatment: taxonomy?.gst_treatment,
        },
        bank_accounts: {
            type: taxonomy?.bank_account_types,
        },
        cash_accounts: {
            cash_type: taxonomy?.cash_account_types,
        },
        inventory: {
            unit: taxonomy?.inventory_uom,
            abc: taxonomy?.inventory_abc,
            movement: taxonomy?.inventory_movement,
        },
        material_issues: {
            unit: taxonomy?.inventory_uom,
            purpose: taxonomy?.consumption_purposes,
        },
    };

    const TEXTAREAS = new Set([
        "category_note", "notes", "billing_address", "shipping_address", "address", "boq_norm",
    ]);
    const CHECKBOXES = new Set([
        "blacklisted", "sweep_configured", "is_default",
    ]);

    const renderField = (c) => {
        const options = DROPDOWNS[entity]?.[c];
        if (options && Array.isArray(options) && options.length > 0) {
            return (
                <select
                    data-testid={`add-input-${c}`}
                    value={row[c] || ""}
                    onChange={(e) => setRow({ ...row, [c]: e.target.value })}
                    className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin text-[var(--text-primary)] focus:border-[var(--brand-amber)] focus:outline-none"
                >
                    <option value="">— Select {c.replace(/_/g, " ")} —</option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        }
        if (isProject && c === "category_note") {
            return (
                <textarea
                    data-testid={`add-input-${c}`}
                    value={row[c] || ""}
                    onChange={(e) => setRow({ ...row, [c]: e.target.value })}
                    rows={2}
                    placeholder="If Category or Sub-Category = 'Others', describe here (free text)"
                    className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-body text-[var(--text-primary)] focus:border-[var(--brand-amber)] focus:outline-none"
                />
            );
        }
        if (TEXTAREAS.has(c)) {
            return (
                <textarea
                    data-testid={`add-input-${c}`}
                    value={row[c] || ""}
                    onChange={(e) => setRow({ ...row, [c]: e.target.value })}
                    rows={2}
                    className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-body text-[var(--text-primary)] focus:border-[var(--brand-amber)] focus:outline-none"
                />
            );
        }
        if (CHECKBOXES.has(c)) {
            return (
                <label className="flex items-center gap-2 text-xs text-[var(--text-primary)]">
                    <input
                        type="checkbox"
                        data-testid={`add-input-${c}`}
                        checked={!!row[c]}
                        onChange={(e) => setRow({ ...row, [c]: e.target.checked })}
                        className="accent-[var(--brand-amber)]"
                    />
                    <span className="text-[var(--text-secondary)]">Enabled / True</span>
                </label>
            );
        }
        return (
            <input
                data-testid={`add-input-${c}`}
                value={row[c] || ""}
                onChange={(e) => setRow({ ...row, [c]: e.target.value })}
                className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
            />
        );
    };

    const isDropdown = (c) => Array.isArray(DROPDOWNS[entity]?.[c]) && DROPDOWNS[entity][c].length > 0;

    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-panel border border-panel max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel p-4 flex items-center justify-between">
                    <div className="font-display font-bold text-[var(--text-primary)]">Add New Row · {entity}</div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-2">
                    {columns.map((c) => (
                        <div key={c}>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">
                                {c} {c === primaryKey && <span className="text-[var(--brand-amber)]">*</span>}
                                {isDropdown(c) && <span className="text-[var(--text-muted)] ml-1">· dropdown</span>}
                                {TEXTAREAS.has(c) && <span className="text-[var(--text-muted)] ml-1">· long text</span>}
                                {CHECKBOXES.has(c) && <span className="text-[var(--text-muted)] ml-1">· yes/no</span>}
                            </div>
                            {renderField(c)}
                        </div>
                    ))}
                    <button
                        data-testid="add-row-save"
                        onClick={save}
                        className="w-full bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center justify-center gap-2 mt-3"
                    >
                        <Save size={12} /> Save Row
                    </button>
                </div>
            </div>
        </div>
    );
}

// ============ CSV Import Modal ============
function ImportCSVModal({ open, onClose, entity, onDone }) {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const upload = async () => {
        if (!file) { toast.error("Pick a CSV file"); return; }
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await fetch(`${API}/data/${entity}/import-csv`, { method: "POST", body: fd });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || "Import failed");
            toast.success(`Imported ${data.imported} rows into ${entity}`);
            onDone();
            onClose();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-panel border border-panel max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel p-4 flex items-center justify-between">
                    <div className="font-display font-bold text-[var(--text-primary)]">Import CSV → {entity}</div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-3">
                    <div className="text-xs text-[var(--text-primary)]/80">First row must be column headers matching the entity keys. Existing data will be replaced.</div>
                    <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => setFile(e.target.files[0])}
                        data-testid="csv-file-input"
                        className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin"
                    />
                    {file && <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--pos)]">✓ {file.name}</div>}
                    <button
                        data-testid="csv-upload-btn"
                        onClick={upload}
                        disabled={busy || !file}
                        className="w-full bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50"
                    >
                        {busy ? "Uploading…" : "Import & Replace"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ============ Bulk AI-Fill Modal ============
function AIFillModal({ open, onClose, entity, primaryKey, columns, selectedRows, onApplied }) {
    const [agents, setAgents] = useState([]);
    const [agentKey, setAgentKey] = useState("project_manager");
    const [pickedCols, setPickedCols] = useState([]);
    const [instruction, setInstruction] = useState("");
    const [busy, setBusy] = useState(false);
    const [predictions, setPredictions] = useState(null);
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        if (open) {
            api.get("/agents").then((r) => setAgents(r.data.agents || []));
            setPickedCols([]);
            setInstruction("Predict next-period values based on the current trend and construction industry benchmarks.");
            setPredictions(null);
        }
    }, [open]);

    const numericCols = columns.filter((c) =>
        selectedRows.some((r) => typeof r[c] === "number")
        && c !== primaryKey && !c.startsWith("_")
    );

    const togglePick = (col) => {
        setPickedCols((cur) => cur.includes(col) ? cur.filter((c) => c !== col) : [...cur, col]);
    };

    const runFill = async () => {
        if (!pickedCols.length) { toast.error("Pick at least one column to predict"); return; }
        setBusy(true);
        setPredictions(null);
        try {
            const r = await api.post(`/data/${entity}/ai-fill`, {
                row_ids: selectedRows.map((r) => r[primaryKey]),
                columns: pickedCols,
                instruction: instruction || "Predict values based on trend",
                agent_persona: agentKey,
            });
            setPredictions(r.data.predictions || []);
            if (!r.data.predictions?.length) toast.info("AI returned no predictions");
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI-Fill failed");
        } finally {
            setBusy(false);
        }
    };

    const applyAll = async () => {
        if (!predictions?.length) return;
        setApplying(true);
        let ok = 0, fail = 0;
        for (const pred of predictions) {
            const row = selectedRows.find((r) => String(r[primaryKey]) === String(pred.id));
            if (!row) continue;
            const merged = { ...row, ...pred.predictions };
            try {
                await api.put(`/data/${entity}`, { item: merged });
                ok += 1;
            } catch (e) {
                fail += 1;
            }
        }
        setApplying(false);
        toast.success(`Applied ${ok} row(s)${fail ? ` · ${fail} failed` : ""} · agents will use fresh data`);
        onApplied?.();
        onClose();
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-panel border border-panel max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Wand2 className="text-[var(--brand-amber)]" size={16} />
                        <div className="font-display font-bold text-[var(--text-primary)]">Bulk AI-Fill · {selectedRows.length} row(s)</div>
                    </div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
                </div>

                <div className="p-4 space-y-3">
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Agent Persona</div>
                        <select
                            data-testid="ai-fill-agent"
                            value={agentKey}
                            onChange={(e) => setAgentKey(e.target.value)}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin"
                        >
                            {agents.map((a) => <option key={a.key} value={a.key}>{a.title}</option>)}
                        </select>
                    </div>

                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">
                            Columns to Predict ({pickedCols.length} picked)
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {numericCols.map((c) => (
                                <button
                                    key={c}
                                    data-testid={`ai-fill-col-${c}`}
                                    onClick={() => togglePick(c)}
                                    className={`px-2.5 py-1 text-[10px] font-mono-fin uppercase tracking-wider border transition-colors ${pickedCols.includes(c) ? "bg-[var(--brand-amber)] text-black border-[var(--brand-amber)]" : "border-panel text-[var(--text-primary)]/70 hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)]"}`}
                                >
                                    {c}
                                </button>
                            ))}
                            {numericCols.length === 0 && (
                                <div className="text-xs text-[var(--text-secondary)]">No numeric columns available in selected rows.</div>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Instruction / Context</div>
                        <textarea
                            data-testid="ai-fill-instruction"
                            value={instruction}
                            onChange={(e) => setInstruction(e.target.value)}
                            rows={2}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-body focus:border-[var(--brand-amber)] focus:outline-none"
                        />
                    </div>

                    <button
                        data-testid="ai-fill-run"
                        onClick={runFill}
                        disabled={busy || !pickedCols.length}
                        className="bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        <Wand2 size={12} /> {busy ? "Generating…" : "Predict Values"}
                    </button>

                    {predictions && (
                        <div className="border border-panel">
                            <div className="p-3 border-b border-panel">
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)]">// AI Predictions · Review Before Applying</div>
                            </div>
                            <table className="w-full text-xs font-body">
                                <thead>
                                    <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                        <th className="text-left py-1.5 px-2">Row ID</th>
                                        {pickedCols.map((c) => (
                                            <th key={c} className="text-right py-1.5 px-2">Old {c}</th>
                                        ))}
                                        {pickedCols.map((c) => (
                                            <th key={"n"+c} className="text-right py-1.5 px-2 text-[var(--brand-amber)]">New {c}</th>
                                        ))}
                                        <th className="text-left py-1.5 px-2">Reasoning</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {predictions.map((p, i) => {
                                        const row = selectedRows.find((r) => String(r[primaryKey]) === String(p.id));
                                        if (!row) return null;
                                        return (
                                            <tr key={p.id} data-testid={`ai-pred-${p.id}`} className="border-b border-panel/60">
                                                <td className="py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{p.id}</td>
                                                {pickedCols.map((c) => (
                                                    <td key={c} className="text-right py-1.5 px-2 font-mono-fin text-[var(--text-primary)]/60">{formatCell(row[c], c)}</td>
                                                ))}
                                                {pickedCols.map((c) => (
                                                    <td key={"n"+c} className="text-right py-1.5 px-2 font-mono-fin text-[var(--pos)]">{formatCell(p.predictions?.[c], c)}</td>
                                                ))}
                                                <td className="py-1.5 px-2 text-[var(--text-primary)]/80">{p.reasoning || "—"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div className="p-3 border-t border-panel flex justify-end">
                                <button
                                    data-testid="ai-fill-apply"
                                    onClick={applyAll}
                                    disabled={applying}
                                    className="bg-[var(--pos)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:brightness-125 transition-all disabled:opacity-50 flex items-center gap-2"
                                >
                                    <Check size={12} /> {applying ? "Applying…" : `Apply ${predictions.length} Row(s)`}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============ Main Entity Table ============
function EntityTable({ entity, primaryKey, reloadCounts }) {
    const [items, setItems] = useState([]);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [csvOpen, setCsvOpen] = useState(false);
    const [askRow, setAskRow] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [aiFillOpen, setAiFillOpen] = useState(false);
    const [taxonomy, setTaxonomy] = useState(null);

    // Extra columns to always surface for specific entities (even when data doesn't have them yet)
    const EXTRA_COLS = {
        projects: ["category", "sub_category", "category_note"],
        vendors: [
            "sub_category", "contact_person", "phone", "email", "address",
            "pan", "tan", "msme_status", "gst_treatment", "tds_section",
            "credit_limit", "opening_payable",
            "bank_name", "account_no", "ifsc",
            "risk_grade", "blacklisted", "onboarded", "last_txn", "notes",
        ],
        clients: [
            "sector", "contact_person", "designation", "phone", "email", "alt_contact",
            "billing_address", "shipping_address",
            "pan", "cin", "credit_limit", "retention_pct", "security_deposit",
            "tds_receivable_rate", "gst_treatment", "msme_status",
            "bank_name", "account_no", "ifsc",
            "onboarded", "last_txn", "notes",
        ],
        bank_accounts: [
            "ifsc", "account_holder", "opening_balance", "opening_date",
            "sanctioned_limit", "drawing_power", "interest_rate", "is_default",
            "rm_name", "rm_phone", "rm_email", "bank_gstin", "sweep_configured", "notes",
        ],
        cash_accounts: [
            "cash_type", "custodian_id", "custodian_phone", "department", "project_id",
            "opening_balance", "opening_date", "reorder_threshold", "notes",
        ],
        inventory: [
            "item_code", "category", "hsn", "min_stock", "max_stock",
            "last_purchase_rate", "avg_rate", "warehouse", "preferred_vendor_id",
            "gst_rate", "boq_norm", "notes",
        ],
        material_issues: [
            "voucher_no", "date", "project_id", "item_id", "item_name", "unit",
            "qty_issued", "rate", "value", "purpose", "issued_to",
            "boq_variance_pct", "approver", "notes",
        ],
    };

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/data/${entity}`);
            setItems(r.data.items || []);
            const dataCols = r.data.items[0] ? Object.keys(r.data.items[0]).filter(k => !k.startsWith("_")) : [];
            const extras = (EXTRA_COLS[entity] || []).filter(c => !dataCols.includes(c));
            setColumns([...dataCols, ...extras]);
            setSelected(new Set());
        } catch (e) {
            toast.error("Load failed: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch taxonomy once — used for project category dropdowns
    useEffect(() => {
        api.get("/settings/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [entity]);

    const saveCell = async (row, column, newValue) => {
        const updated = { ...row, [column]: newValue };
        try {
            await api.put(`/data/${entity}`, { item: updated });
            toast.success("Saved · agents will see this on next refresh");
            load();
            reloadCounts?.();
        } catch (e) {
            toast.error("Save failed: " + (e.response?.data?.detail || e.message));
        }
    };

    const addRow = async (row) => {
        try {
            await api.put(`/data/${entity}`, { item: row });
            toast.success("Row added · agents will see this");
            load();
            reloadCounts?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const deleteRow = async (id) => {
        if (!window.confirm(`Delete row ${id}?`)) return;
        try {
            await api.delete(`/data/${entity}/${encodeURIComponent(id)}`);
            toast.success("Deleted · agents will see this");
            load();
            reloadCounts?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const resetEntity = async () => {
        if (!window.confirm(`Reset ${entity} to demo defaults? Your edits will be lost.`)) return;
        try {
            const r = await api.post(`/data/${entity}/reset`);
            toast.success(`Restored ${r.data.restored_count} default rows`);
            load();
            reloadCounts?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const exportCSV = () => {
        if (!items.length) return;
        const rows = [columns.join(",")];
        for (const it of items) {
            const line = columns.map((c) => {
                const v = it[c];
                if (v === null || v === undefined) return "";
                const s = String(v);
                return s.includes(",") || s.includes("\"") ? `"${s.replace(/"/g, '""')}"` : s;
            }).join(",");
            rows.push(line);
        }
        const blob = new Blob([rows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${entity}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const filtered = search.trim()
        ? items.filter((r) => JSON.stringify(r).toLowerCase().includes(search.toLowerCase()))
        : items;

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5 border border-panel bg-[var(--bg-base)] px-3 py-1.5 flex-1 min-w-[200px] max-w-md">
                    <Search size={12} className="text-[var(--text-secondary)]" />
                    <input
                        data-testid={`search-${entity}`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={`Search ${filtered.length}/${items.length} rows…`}
                        className="bg-transparent flex-1 text-xs font-mono-fin focus:outline-none"
                    />
                </div>
                <button data-testid={`add-btn-${entity}`} onClick={() => setAddOpen(true)}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1.5">
                    <Plus size={12} /> Add
                </button>
                <button data-testid={`csv-btn-${entity}`} onClick={() => setCsvOpen(true)}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1.5">
                    <Upload size={12} /> Import CSV
                </button>
                <button onClick={exportCSV}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1.5">
                    <Download size={12} /> Export
                </button>
                <button data-testid={`refresh-${entity}`} onClick={load} disabled={loading}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1.5 disabled:opacity-50">
                    <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
                </button>
                <button data-testid={`reset-${entity}`} onClick={resetEntity}
                    className="border border-[var(--neg)]/40 text-[var(--neg)] px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:bg-[var(--neg)]/10 transition-colors flex items-center gap-1.5">
                    <RotateCcw size={12} /> Reset Demo
                </button>
                {selected.size > 0 && (
                    <button
                        data-testid={`ai-fill-btn-${entity}`}
                        onClick={() => setAiFillOpen(true)}
                        className="border border-[var(--brand-amber)] text-black bg-[var(--brand-amber)] px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center gap-1.5"
                    >
                        <Wand2 size={12} /> AI Fill · {selected.size}
                    </button>
                )}
            </div>

            <Panel testId={`table-${entity}`} title={`${entity.toUpperCase()} · ${filtered.length}`} subtitle="Double-click any cell to edit · changes flow to all AI agents">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs font-body">
                        <thead>
                            <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-center py-2 px-2 w-8">
                                    <input
                                        type="checkbox"
                                        data-testid={`select-all-${entity}`}
                                        checked={filtered.length > 0 && filtered.every((r) => selected.has(r[primaryKey]))}
                                        onChange={(e) => {
                                            const s = new Set(selected);
                                            if (e.target.checked) filtered.forEach((r) => s.add(r[primaryKey]));
                                            else filtered.forEach((r) => s.delete(r[primaryKey]));
                                            setSelected(s);
                                        }}
                                        className="accent-[var(--brand-amber)]"
                                    />
                                </th>
                                {columns.map((c) => (
                                    <th key={c} className={`text-left py-2 px-2 ${c === primaryKey ? "text-[var(--brand-amber)]" : ""}`}>
                                        {c}
                                    </th>
                                ))}
                                <th className="text-right py-2 px-2 w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((row, idx) => {
                                const id = row[primaryKey];
                                const isSel = selected.has(id);
                                return (
                                    <tr key={id} data-testid={`row-${entity}-${id}`} className={`border-b border-panel/60 hover:bg-panel/40 ${isSel ? "bg-[var(--brand-amber-dim)]" : ""}`}>
                                        <td className="text-center py-1.5 px-2">
                                            <input
                                                type="checkbox"
                                                data-testid={`select-${entity}-${id}`}
                                                checked={isSel}
                                                onChange={(e) => {
                                                    const s = new Set(selected);
                                                    if (e.target.checked) s.add(id); else s.delete(id);
                                                    setSelected(s);
                                                }}
                                                className="accent-[var(--brand-amber)]"
                                            />
                                        </td>
                                        {columns.map((c) => (
                                            <td key={c} className="py-1.5 px-2">
                                                <EditableCell
                                                    value={row[c]}
                                                    columnName={c}
                                                    onSave={(v) => saveCell(row, c, v)}
                                                    testId={`cell-${entity}-${id}-${c}`}
                                                />
                                            </td>
                                        ))}
                                        <td className="py-1.5 px-2 text-right whitespace-nowrap">
                                            {LEDGER_KIND[entity] && (
                                                <a
                                                    data-testid={`ledger-${entity}-${id}`}
                                                    href={`/accounts/${LEDGER_KIND[entity]}/${id}`}
                                                    title="View full account ledger"
                                                    className="text-[var(--pos)] hover:text-[var(--text-primary)] p-1 inline-block"
                                                >
                                                    <BookOpen size={12} />
                                                </a>
                                            )}
                                            <button
                                                data-testid={`ask-${entity}-${id}`}
                                                onClick={() => setAskRow(row)}
                                                title="Ask AI Agent about this row"
                                                className="text-[var(--brand-amber)] hover:text-[var(--text-primary)] p-1"
                                            >
                                                <Bot size={12} />
                                            </button>
                                            <button
                                                data-testid={`delete-${entity}-${id}`}
                                                onClick={() => deleteRow(id)}
                                                title="Delete row"
                                                className="text-[var(--neg)] hover:text-[var(--text-primary)] p-1"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr><td colSpan={columns.length + 2} className="text-[var(--text-secondary)] py-6 text-center">
                                    {loading ? "Loading…" : "No rows match your search."}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Panel>

            <AddRowModal open={addOpen} onClose={() => setAddOpen(false)} columns={columns} primaryKey={primaryKey} onSave={addRow} entity={entity} taxonomy={taxonomy} />
            <ImportCSVModal open={csvOpen} onClose={() => setCsvOpen(false)} entity={entity} onDone={load} />
            <AskAgentModal open={!!askRow} onClose={() => setAskRow(null)} row={askRow} entity={entity} />
            <AIFillModal
                open={aiFillOpen}
                onClose={() => setAiFillOpen(false)}
                entity={entity}
                primaryKey={primaryKey}
                columns={columns}
                selectedRows={items.filter((r) => selected.has(r[primaryKey]))}
                onApplied={load}
            />
        </div>
    );
}

// ============ Main Page ============
export default function DataManager() {
    const [tab, setTab] = useState("projects");
    const [entities, setEntities] = useState([]);

    const load = async () => {
        try {
            const r = await api.get("/data/entities");
            setEntities(r.data.entities || []);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => { load(); }, []);

    const active = entities.find((e) => e.key === tab);

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// DATA MANAGER</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <Database className="text-[var(--brand-amber)]" size={26} />
                    Universal Data Manager
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Add · edit · delete · CSV import — every write flows to all 10 AI agents live
                </p>
            </div>

            <div className="flex gap-1 border border-panel bg-panel p-1 overflow-x-auto" data-testid="dm-tabs">
                {entities.map((e) => (
                    <button
                        key={e.key}
                        data-testid={`dm-tab-${e.key}`}
                        onClick={() => setTab(e.key)}
                        className={`px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider whitespace-nowrap transition-colors ${tab === e.key ? "bg-[var(--brand-amber)] text-black" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                    >
                        {e.label} <span className="opacity-60">({e.count})</span>
                    </button>
                ))}
            </div>

            {active && (
                <EntityTable
                    key={tab}
                    entity={tab}
                    primaryKey={active.primary_key}
                    reloadCounts={load}
                />
            )}
        </div>
    );
}
